import { useState, useCallback, useRef, useEffect } from "react";
import { PopupModalRef } from "../../../common/Modal/Modal";
import { LocationData } from "../types";
import { GeolocationErrorCode } from "../../../../utils/constants";
import { ErrorToaster } from "../../../../helpers/toasterHelper";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { logErrorDev } from "../../../../utils/formatters";

/**
 * Custom hook for LocationSelectorField component logic
 * Manages location state, geolocation API interactions, and modal control.
 * Provides methods for fetching user's current location and handling map picker.
 * @param {LocationData | null} initialLocation - Initial location value
 * @param {(location: LocationData) => void} onLocationChange - Callback for location changes
 * @returns {object} Location utilities including state, handlers, and modal ref
 */
export const useLocationSelector = (
  initialLocation: LocationData | null,
  onLocationChange?: (location: LocationData) => void
) => {
  const [location, setLocation] = useState<LocationData | null>(
    initialLocation
  );
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const modalRef = useRef<PopupModalRef>(null);

  useEffect(() => {
    setLocation(initialLocation);
  }, [initialLocation]);

  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      ErrorToaster(ERROR_MESSAGES.LOCATION.GEOLOCATION_NOT_SUPPORTED  );
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );

          let address: string;
          if (response.ok) {
            const data = await response.json();
            if (data && data.display_name) {
              address = data.display_name;
            } else {
              address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            }
          } else {
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }

          const locationData: LocationData = {
            lat: latitude,
            lng: longitude,
            address: address,
          };

          setLocation(locationData);
          onLocationChange?.(locationData);
        } catch (error) {
          logErrorDev(ERROR_MESSAGES.LOCATION.COULD_NOT_FETCH_LOCATION, error);
          const fallbackAddress = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
          const locationData: LocationData = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: fallbackAddress,
          };
          setLocation(locationData);
          onLocationChange?.(locationData);
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        logErrorDev(ERROR_MESSAGES.LOCATION.COULD_NOT_FETCH_LOCATION, error);
        setIsLoadingLocation(false);

        switch (error.code) {
        case GeolocationErrorCode.PERMISSION_DENIED:
          ErrorToaster(ERROR_MESSAGES.LOCATION.LOCATION_ACCESS_DENIED);
          break;
        case GeolocationErrorCode.POSITION_UNAVAILABLE:
          ErrorToaster(ERROR_MESSAGES.LOCATION.LOCATION_INFORMATION_UNAVAILABLE);
          break;
        case GeolocationErrorCode.TIMEOUT:
          ErrorToaster(ERROR_MESSAGES.LOCATION.LOCATION_REQUEST_TIMED_OUT);
          break;
        default:
          ErrorToaster(ERROR_MESSAGES.LOCATION.COULD_NOT_FETCH_LOCATION);
          break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [onLocationChange]);

  const openMapPicker = useCallback(() => {
    modalRef.current?.openModal();
  }, []);

  const handleMapSelect = useCallback(
    (picked: LocationData) => {
      const locationData: LocationData = {
        lat: picked.lat,
        lng: picked.lng,
        address: picked.address || "",
      };
      setLocation(locationData);
      onLocationChange?.(locationData);
      modalRef.current?.closeModal();
    },
    [onLocationChange]
  );

  return {
    location,
    isLoadingLocation,
    modalRef,
    fetchUserLocation,
    openMapPicker,
    handleMapSelect,
  };
};
